export const IMPACT_STORAGE_KEY = 'airies_impact_click'

const TTL_MS = 90 * 24 * 60 * 60 * 1000
const MAX_CLICK_ID_LENGTH = 128

type ImpactStoragePayload = {
  clickId: string
  capturedAt: number
  expiresAt: number
}

function hasWindow(): boolean {
  return typeof window !== 'undefined'
}

function normalizeClickId(value: string | null): string | null {
  const normalized = value?.trim()
  if (!normalized || normalized.length > MAX_CLICK_ID_LENGTH) return null
  return normalized
}

function removeFromStorage(storage: Storage) {
  try {
    storage.removeItem(IMPACT_STORAGE_KEY)
  } catch {
    // Ignore storage failures so attribution never breaks user flows.
  }
}

function persistPayload(payload: ImpactStoragePayload) {
  if (!hasWindow()) return

  try {
    window.localStorage.setItem(IMPACT_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore localStorage failures and still try sessionStorage.
  }

  try {
    window.sessionStorage.setItem(IMPACT_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore sessionStorage failures to keep flow resilient.
  }
}

function readPayload(storage: Storage): ImpactStoragePayload | null {
  try {
    const raw = storage.getItem(IMPACT_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<ImpactStoragePayload>
    const clickId = normalizeClickId(typeof parsed.clickId === 'string' ? parsed.clickId : null)
    const capturedAt = typeof parsed.capturedAt === 'number' ? parsed.capturedAt : NaN
    const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : NaN

    if (
      !clickId ||
      !Number.isFinite(capturedAt) ||
      !Number.isFinite(expiresAt) ||
      capturedAt <= 0 ||
      expiresAt <= capturedAt
    ) {
      return null
    }

    return { clickId, capturedAt, expiresAt }
  } catch {
    return null
  }
}

function hasStoredValue(storage: Storage): boolean {
  try {
    return storage.getItem(IMPACT_STORAGE_KEY) !== null
  } catch {
    return false
  }
}

function isExpired(payload: ImpactStoragePayload): boolean {
  return Date.now() >= payload.expiresAt
}

export function clearImpactClickId(): void {
  if (!hasWindow()) return
  removeFromStorage(window.localStorage)
  removeFromStorage(window.sessionStorage)
}

function clearInvalidOrExpiredImpact(): void {
  if (!hasWindow()) return

  const localPayload = readPayload(window.localStorage)
  const sessionPayload = readPayload(window.sessionStorage)
  const shouldClear =
    (localPayload !== null && isExpired(localPayload)) ||
    (sessionPayload !== null && isExpired(sessionPayload)) ||
    (hasStoredValue(window.localStorage) && localPayload === null) ||
    (hasStoredValue(window.sessionStorage) && sessionPayload === null)

  if (shouldClear) clearImpactClickId()
}

export function captureImpactFromLocation(search?: string): void {
  if (!hasWindow()) return

  clearInvalidOrExpiredImpact()

  try {
    const params = new URLSearchParams(search ?? window.location.search)
    const clickId = normalizeClickId(params.get('im_ref'))
    if (!clickId) return

    const capturedAt = Date.now()
    persistPayload({
      clickId,
      capturedAt,
      expiresAt: capturedAt + TTL_MS,
    })
  } catch {
    // Ignore URL parsing failures to avoid affecting navigation.
  }
}

export function getImpactClickId(): string | null {
  if (!hasWindow()) return null

  clearInvalidOrExpiredImpact()

  const localPayload = readPayload(window.localStorage)
  if (localPayload && !isExpired(localPayload)) return localPayload.clickId

  const sessionPayload = readPayload(window.sessionStorage)
  if (sessionPayload && !isExpired(sessionPayload)) return sessionPayload.clickId

  return null
}

export function appendImpactToUrl(url: string): string {
  const clickId = getImpactClickId()
  if (!clickId) return url

  try {
    const parsedUrl = new URL(url)
    if (parsedUrl.searchParams.has('x-impact-clickid')) return url
    parsedUrl.searchParams.set('x-impact-clickid', clickId)
    return parsedUrl.toString()
  } catch {
    return url
  }
}

if (import.meta.env.DEV && hasWindow()) {
  window.__airiesImpact = {
    getImpactClickId,
    clearImpactClickId,
  }
}
